import { type JSX } from 'react';

import { Image, StyleSheet, Text, View } from '@react-pdf/renderer';

import { type PdfCompetencyTableData, type PdfLineChartData } from '../benchmarkingReport.types';
import { ReportPage } from '../components/ReportPage';
import { FACE_ICON_NAMES } from '../lib/pdfConstants';
import { PDF_BORDER_RADIUS, PDF_FONT_WEIGHTS, PDF_FONTS, PDF_PAGE, PDF_SPACING, devOnly } from '../lib/pdfTokens';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TEXT_PRIMARY = '#25252A';
const TEXT_SECONDARY = '#5E5E69';
const CARD_BG = '#FFFFFF';
const CARD_BORDER = '#D9D9E3';
const ANALYSIS_BG = '#C3F2DC';
const ANALYSIS_BOX_W = 180;
const CHART_W = 303;
const CHART_H = 210;

// Competency table
const BAR_SCHOOL_COLOR = '#0A7A71'; // teal-strong — média da escola na Red1000
const BAR_META_COLOR = '#B485FF'; // purple-soft — meta da escola
const TABLE_HEADER_BG = '#F5F5F8';
const TABLE_ROW_BORDER = '#EBEBF0';

// Inline SVG divider — 2×12 vertical line matching the Figma spec
function makeDividerUri(): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 2 12"><path stroke="#d9d9e3" stroke-linecap="round" stroke-width="2" d="M1 1v10"/></svg>`;
  const encoded = btoa(
    encodeURIComponent(svg).replace(/%([0-9A-F]{2})/g, (_, hex: string) => String.fromCharCode(parseInt(hex, 16)))
  );
  return `data:image/svg+xml;base64,${encoded}`;
}

const COLUMN_DIVIDER_URI = makeDividerUri();

const FALLBACK_ESSAY_EVOLUTION =
  'A evolução do desempenho ao longo das aplicações de redação indica crescimento progressivo em todas as turmas do 3º ano, com ganhos mais expressivos a partir da 4ª prática. A média da escola mantém-se acima da referência do ENEM em todas as tarefas, sinalizando consolidação da aprendizagem. Recomenda-se a continuidade do volume de práticas para sustentar a tendência de avanço observada.';

const FALLBACK_COMPETENCY =
  'A análise por competência mostra que a escola já atinge ou supera a média do ENEM 2025 em algumas habilidades, mas ainda apresenta competências abaixo dessa referência. O principal potencial de crescimento está em concentrar esforços pedagógicos nessas competências específicas — com reforço de conteúdos, prática direcionada e feedback focado.';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  // ── Line chart card ──────────────────────────────────────────────────────
  card: {
    backgroundColor: CARD_BG,
    borderRadius: PDF_BORDER_RADIUS.md,
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 2,
    borderLeftWidth: 1,
    borderStyle: 'solid',
    borderColor: CARD_BORDER,
    padding: PDF_SPACING[3],
    marginHorizontal: -PDF_PAGE.cardBleed,
    marginBottom: PDF_SPACING[2],
  },
  cardRow: {
    flexDirection: 'row',
    gap: PDF_SPACING[3],
  },
  cardLeft: { flex: 1 },
  cardTitle: {
    fontSize: 12,
    fontWeight: PDF_FONT_WEIGHTS.bold,
    fontFamily: PDF_FONTS.sans,
    color: TEXT_PRIMARY,
    marginBottom: 2,
    lineHeight: 1.35,
  },
  cardSubtitle: {
    fontSize: 9,
    fontWeight: 400,
    fontFamily: PDF_FONTS.sans,
    color: TEXT_SECONDARY,
    lineHeight: 1.2,
    marginBottom: PDF_SPACING[6],
  },
  cardRight: {
    width: ANALYSIS_BOX_W,
    backgroundColor: ANALYSIS_BG,
    borderRadius: PDF_BORDER_RADIUS.sm,
    padding: PDF_SPACING[3],
  },
  analysisIconRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: PDF_SPACING[3],
  },
  analysisIconBox: {
    width: 24,
    height: 24,
    backgroundColor: '#FFFFFF',
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
  },
  analysisText: {
    fontSize: 8.5,
    fontWeight: 400,
    fontFamily: PDF_FONTS.sans,
    color: '#00513D',
    lineHeight: 1.5,
  },

  // Legend dot + label row styles
  chartLegend: {
    flexDirection: 'row' as const,
    flexWrap: 'wrap' as const,
    justifyContent: 'center' as const,
    gap: 8,
    marginTop: 6,
  },
  chartLegendItem: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 4,
  },
  chartLegendDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  chartLegendLabel: {
    fontSize: 8,
    fontWeight: 300,
    fontFamily: PDF_FONTS.sans,
    color: '#5E5E69',
    lineHeight: 1.35,
  },
  tableCard: {
    backgroundColor: CARD_BG,
    borderRadius: PDF_BORDER_RADIUS.md,
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 2,
    borderLeftWidth: 1,
    borderStyle: 'solid',
    borderColor: CARD_BORDER,
    padding: PDF_SPACING[3],
    marginHorizontal: -PDF_PAGE.cardBleed,
  },
  tableCardTitle: {
    fontSize: 12,
    fontWeight: PDF_FONT_WEIGHTS.bold,
    fontFamily: PDF_FONTS.sans,
    color: TEXT_PRIMARY,
    marginBottom: 2,
    lineHeight: 1.35,
  },
  tableCardSubtitle: {
    fontSize: 9,
    fontWeight: 400,
    fontFamily: PDF_FONTS.sans,
    color: TEXT_SECONDARY,
    lineHeight: 1.2,
    marginBottom: PDF_SPACING[3],
  },

  // Table header row
  tableHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: TABLE_HEADER_BG,
    borderRadius: PDF_BORDER_RADIUS.sm,
    paddingHorizontal: PDF_SPACING[2],
    paddingVertical: 6,
    marginBottom: 2,
  },
  tableHeaderLabel: {
    fontSize: 10,
    fontWeight: PDF_FONT_WEIGHTS.semiBold,
    fontFamily: PDF_FONTS.sans,
    color: '#5E5E69',
    lineHeight: 1.35,
  },
  tableHeaderSeparator: {
    fontSize: 7,
    fontFamily: PDF_FONTS.sans,
    color: TEXT_SECONDARY,
    marginHorizontal: 2,
  },
  tableHeaderFaceCol: {
    flex: 1,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  },
  colName: { width: 110 },
  colBars: { flex: 1 },
  // Score col width matches one face icon slot (~32pt): divider(2) + margin(4) + icon(20) + margin(4) = 30
  colScore: { width: 44, alignItems: 'flex-end' as const },

  // Table data rows — all rows have bottom border including last
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: PDF_SPACING[2],
    paddingVertical: 5,
    borderBottomWidth: 1,
    borderBottomColor: TABLE_ROW_BORDER,
    borderStyle: 'solid',
  },
  tableRowName: {
    fontSize: 10,
    fontWeight: 300,
    fontFamily: PDF_FONTS.sans,
    color: '#3F3F47',
    lineHeight: 1.35,
  },
  barFill: {
    height: 10,
    borderRadius: 3,
    marginBottom: 2,
  },
  scoreValue: {
    fontSize: 10,
    fontWeight: PDF_FONT_WEIGHTS.semiBold,
    fontFamily: PDF_FONTS.sans,
    color: BAR_SCHOOL_COLOR,
    lineHeight: 1.35,
    textAlign: 'right' as const,
  },
  scoreValueMeta: {
    fontSize: 10,
    fontWeight: PDF_FONT_WEIGHTS.semiBold,
    fontFamily: PDF_FONTS.sans,
    color: BAR_META_COLOR,
    lineHeight: 1.35,
    textAlign: 'right' as const,
  },

  // Legend row
  tableLegend: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: PDF_SPACING[4],
    marginTop: PDF_SPACING[2],
    marginBottom: PDF_SPACING[3],
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  legendDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
  },
  legendLabel: {
    fontSize: 8,
    fontWeight: 300,
    fontFamily: PDF_FONTS.sans,
    color: TEXT_SECONDARY,
  },

  // Full-width analysis box (bottom of table card)
  fullAnalysisBox: {
    backgroundColor: ANALYSIS_BG,
    borderRadius: PDF_BORDER_RADIUS.sm,
    padding: PDF_SPACING[3],
    flexDirection: 'row',
    gap: PDF_SPACING[3],
    alignItems: 'flex-start',
  },
  fullAnalysisIconBox: {
    width: 24,
    height: 24,
    backgroundColor: '#FFFFFF',
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
    flexShrink: 0,
  },
  fullAnalysisText: {
    flex: 1,
    fontSize: 8.5,
    fontWeight: 400,
    fontFamily: PDF_FONTS.sans,
    color: '#00513D',
    lineHeight: 1.5,
  },
});

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

type LineChartCardProps = {
  chartData?: PdfLineChartData;
  lampIconUri?: string;
};

function LineChartCard({ chartData, lampIconUri }: LineChartCardProps): JSX.Element {
  const analysisText = chartData?.analise ?? devOnly(FALLBACK_ESSAY_EVOLUTION);

  return (
    <View
      style={styles.card}
      wrap={false}>
      <View style={styles.cardRow}>
        <View style={styles.cardLeft}>
          <Text style={styles.cardTitle}>Evolução de desempenho por volume de redações</Text>
          <Text style={styles.cardSubtitle}>Acompanhamento da média da escola em relação à referência ENEM</Text>
          {chartData?.chartUri ? (
            <Image
              src={chartData.chartUri}
              style={{ width: CHART_W, height: CHART_H }}
            />
          ) : (
            <View style={{ width: CHART_W, height: CHART_H }} />
          )}
          {chartData?.legendItems && chartData.legendItems.length > 0 ? (
            <View style={styles.chartLegend}>
              {chartData.legendItems.map((item) => (
                <View
                  key={item.name}
                  style={styles.chartLegendItem}>
                  <View style={[styles.chartLegendDot, { backgroundColor: item.color }]} />
                  <Text style={styles.chartLegendLabel}>{item.name}</Text>
                </View>
              ))}
            </View>
          ) : null}
        </View>
        <View style={styles.cardRight}>
          {lampIconUri ? (
            <View style={styles.analysisIconRow}>
              <View style={styles.analysisIconBox}>
                <Image
                  src={lampIconUri}
                  style={{ width: 16, height: 16 }}
                />
              </View>
            </View>
          ) : null}
          <Text style={styles.analysisText}>{analysisText}</Text>
        </View>
      </View>
    </View>
  );
}

type CompetencyBarProps = {
  value: number | null;
  maxScore: number;
  color: string;
};

function CompetencyBar({ value, maxScore, color }: CompetencyBarProps): JSX.Element {
  const pct = value !== null && maxScore > 0 ? Math.min(value / maxScore, 1) : 0;
  return (
    <View style={styles.barFill}>
      <View style={[styles.barFill, { backgroundColor: color, width: `${Math.round(pct * 100)}%` }]} />
    </View>
  );
}

type CompetencyTableCardProps = {
  tableData?: PdfCompetencyTableData;
  lampIconUri?: string;
  faceIconUris?: string[];
};

function CompetencyTableCard({ tableData, lampIconUri, faceIconUris }: CompetencyTableCardProps): JSX.Element {
  const maxScore = tableData?.maxScore ?? 200;
  const rows = tableData?.rows ?? [];
  const analysisText = tableData?.analise ?? devOnly(FALLBACK_COMPETENCY);

  return (
    <View
      style={styles.tableCard}
      wrap={false}>
      <Text style={styles.tableCardTitle}>Desempenho por competência</Text>
      <Text style={styles.tableCardSubtitle}>Comparativo das médias da escola em cada competência avaliativa</Text>

      {/* Header */}
      <View style={styles.tableHeaderRow}>
        <View style={styles.colName}>
          <Text style={styles.tableHeaderLabel}>Competências</Text>
        </View>
        <View style={[styles.colBars, { flexDirection: 'row', alignItems: 'center' }]}>
          {FACE_ICON_NAMES.map((name, i) => (
            <View
              key={name}
              style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
              <Image
                src={COLUMN_DIVIDER_URI}
                style={{ width: 2, height: 20, marginRight: 4 }}
              />
              <View style={{ flex: 1, alignItems: 'center' }}>
                {faceIconUris?.[i] ? (
                  <Image
                    src={faceIconUris[i]}
                    style={{ width: 20, height: 20 }}
                  />
                ) : (
                  <View style={{ width: 20, height: 20 }} />
                )}
              </View>
            </View>
          ))}
        </View>
        <View style={[styles.colScore, { flexDirection: 'row', alignItems: 'center' }]}>
          <Image
            src={COLUMN_DIVIDER_URI}
            style={{ width: 2, height: 20, marginRight: 4 }}
          />
          <Text style={[styles.tableHeaderLabel, { flex: 1, textAlign: 'right' }]}>Médias</Text>
        </View>
      </View>

      {/* Data rows */}
      {rows.map((row) => (
        <View
          key={row.id}
          style={styles.tableRow}>
          {/* Competency name */}
          <View style={styles.colName}>
            <Text style={styles.tableRowName}>{row.nome}</Text>
          </View>

          {/* Dual bars */}
          <View style={styles.colBars}>
            <CompetencyBar
              value={row.notaMedia}
              maxScore={maxScore}
              color={BAR_SCHOOL_COLOR}
            />
            <CompetencyBar
              value={row.notaMaxima}
              maxScore={maxScore}
              color={BAR_META_COLOR}
            />
          </View>

          {/* Scores */}
          <View style={[styles.colScore, { gap: 1 }]}>
            <Text style={styles.scoreValue}>{row.notaMedia ?? '–'}</Text>
            <Text style={styles.scoreValueMeta}>{row.notaMaxima ?? '–'}</Text>
          </View>
        </View>
      ))}

      {/* Legend */}
      <View style={styles.tableLegend}>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: BAR_SCHOOL_COLOR }]} />
          <Text style={styles.legendLabel}>Média da escola na Red1000</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: BAR_META_COLOR }]} />
          <Text style={styles.legendLabel}>Meta da Escola</Text>
        </View>
      </View>

      {/* Full-width analysis box */}
      <View style={styles.fullAnalysisBox}>
        {lampIconUri ? (
          <View style={styles.fullAnalysisIconBox}>
            <Image
              src={lampIconUri}
              style={{ width: 16, height: 16 }}
            />
          </View>
        ) : null}
        <Text style={styles.fullAnalysisText}>{analysisText}</Text>
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type EssayEvolutionPageProps = {
  essayEvolution?: PdfLineChartData;
  competencyTable?: PdfCompetencyTableData;
  lampIconUri?: string;
  faceIconUris?: string[];
};

export function EssayEvolutionPage({
  essayEvolution,
  competencyTable,
  lampIconUri,
  faceIconUris,
}: EssayEvolutionPageProps): JSX.Element {
  return (
    <ReportPage>
      <LineChartCard
        chartData={essayEvolution}
        lampIconUri={lampIconUri}
      />
      <CompetencyTableCard
        tableData={competencyTable}
        lampIconUri={lampIconUri}
        faceIconUris={faceIconUris}
      />
    </ReportPage>
  );
}
